import { useEffect, useReducer, useRef, useState } from "react";
import { useConnection } from "../Ros/Connection";
import { useRobotDetails } from "../../utilities/RobotDetailsContext";

type Params = {
    param : string;
    value : string | number;
}

interface State<T> {
    status : "init" | "fetching" | "error" | "fetched";  
    data?: T;
    error?: Error;
}

interface ReturnFetch<T> {
    state : State<T>;
    get : (params? : Array<Params>) => void;
    post : (body? : T) => void;
    abortRequest : (() => void | undefined) | undefined;
}

type Action<T> = 
    | { type: 'loading'}
    | { type: 'fetched'; payload: T}
    | { type: 'error'; payload: Error}
    
type ResponseType = "json" | "blob" | "text";

export function useFetch<T=unknown>(url: RequestInfo, options? : RequestInit, responseType : ResponseType = 'json'): ReturnFetch<T> {
    const {robotDetails} = useRobotDetails();
    const initialState: State<T> = {
        status : "init",
        error : undefined,
        data : undefined,
    }

    const fetchReducer = (state: State<T>, action: Action<T>): State<T> => {
        switch(action.type){
            case 'loading':
                return {...state, status : "fetching", data : undefined, error : undefined};
            case 'fetched':
                return {...state, status : "fetched", data : action.payload};
            case 'error':
                return {...state, status : "error", error : action.payload};
            default:
                return state;
        }
    }

    const [state, dispatch] = useReducer(fetchReducer, initialState);
    const connected = useConnection()
    let default_url = robotDetails.baseUrl
    if (!connected){
        default_url = robotDetails.baseUrl
    }
    const [useBackup, setUseBackup] = useState<string>(default_url)
    const abort = useRef<AbortController>();
    const original_url = url
    const cancelRequest = useRef<boolean>(false);

    useEffect(() => {
        console.log("Connection: ",connected)
        setUseBackup(connected ? robotDetails.baseUrl : robotDetails.baseUrl);
        default_url = robotDetails.baseUrl
    }, [connected]);

    const get = async (params? : Array<Params>) => {

        if (!options || options.method === 'GET' || !options.method) {
            options = options || {};
                options.headers = {
                    ...(options.headers || {}),
                    'X-Secret-Key': robotDetails.verificationPass,
                    'robot' : robotDetails.robotName,
                };
            // Default request is GET if method is not given
            
            if (params && params.length > 0) {
                url = original_url + '?';  
                // console.log(params,params.length,url)
                // Append query parameters to the URL
                for (let i = 0; i < params.length; i++) {
                    url += `${params[i].param}=${encodeURIComponent(params[i].value)}`;
                    if (i !== params.length - 1) {
                        url += '&';
                    }
                }
            }
        }
        request(url, options,responseType);
    }

    const post = (body? : T | undefined) => {
        let _options = {};

        if(body){
            _options = {
                ...options, 
                body : JSON.stringify(body),
                headers:  {
                    'Content-Type':'application/json' ,
                    'X-Secret-Key': robotDetails.verificationPass,
                    'robot' : robotDetails.robotName,
                }
            }
        } else {
            _options = {
                ...options,
                headers:  {
                    'Content-Type':'application/json' ,
                    'X-Secret-Key': robotDetails.verificationPass,
                    'robot' : robotDetails.robotName,
                }
            }
        }
        request(url, _options);
    }

    const request = async (url : RequestInfo, options? : RequestInit, responseType: ResponseType = "json") => {
        dispatch({type : 'loading'});
        // console.log("Loading");
        cancelRequest.current = false;

        const abortController = new AbortController();
        const signal = abortController.signal;
        abort.current = abortController;

        const newUrl = (url === '/api/missions/sync_time' || url === '/api/missions/sync_users')? robotDetails.baseUrl + url: useBackup + url;
        // console.log(newUrl,connected,url)

        try {
            const res = await fetch(newUrl, {...options, signal});
            if(!res.ok){
                throw new Error(res.statusText);
            }

            let data: unknown;

            switch (responseType) {
            case "blob":
                data = await res.blob();
                break;
            case "text":
                data = await res.text();
                break;
            default:
                data = await res.json();
                break;
            }
            

            if(cancelRequest.current) return;
            // console.log("Fetched!");
            dispatch({ type: "fetched", payload: data as T });
        } catch (e){
            if(cancelRequest.current) return;
            console.log(e);
            dispatch({type: 'error', payload: e as Error});
        } finally {
            abort.current = undefined;
        }
    }

    const abortRequest = () => {
        cancelRequest.current = true;
        if(abort.current){
            abort.current.abort();
        }
    }
    

    return { state, get, post, abortRequest};
}
    
